import { format } from "date-fns";
import {
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Cpu,
  ExternalLink,
  KeyRound,
  LockKeyhole,
  Package,
  Plus,
  Upload,
} from "lucide-react";
import { ReactNode, useState } from "react";

import {
  IgButton,
  IgLink,
  IgMonoText,
  IgPaper,
  IgTextField,
  IgTypography,
} from "@intelligems/ig-theme";
import { IconButton, Tab, Tabs, Tooltip, useTheme } from "@mui/material";
import Stack from "@mui/material/Stack";

import type {
  WorkspaceAiChatConfig,
  WorkspaceControlsPanelConfig,
} from "../../domainConfig";
import {
  DEFAULT_DEPENDENCY_VERSION,
  useAddDependencyForm,
  validateNewDependencyName,
} from "../../hooks/useDependencyForm";
import type { WorkspaceDependency, WorkspaceVersion } from "../../types";
import { IgFontFamily, IgGrayPrimitives } from "@intelligems/ig-design-tokens";
import { useImmer } from "use-immer";
import { WorkspaceAiChat } from "./WorkspaceAiChat";

const PANEL_WIDTH = 420;
/** Width of the collapsed rail that replaces the panel. */
const RAIL_WIDTH = 34;

/** Zone 3 left — the workspace controls panel, left of the file list. */
export interface ControlsPanelProps {
  /** Runtime facts + the runtime-contract prose forwarded to the AI chat. */
  config: WorkspaceControlsPanelConfig;
  /** `null` for domains with no authoring assistant; hides the AI Chat tab. */
  aiChat: WorkspaceAiChatConfig | null;
  /** The live editor buffer, sent to the AI chat as context. */
  editorValue: string;
  dependencies: WorkspaceDependency[];
  onAddDependency: (dependency: WorkspaceDependency) => void;
  onRemoveDependency: (name: string) => void;
  onUpdateDependencyVersion: (name: string, version: string) => void;
  availableSecretNames: string[];
  onManageSecrets: () => void;
  onApplySource: (source: string) => void;
  onAiPendingChange: (isPending: boolean) => void;
  activeVersion: WorkspaceVersion | null;
  versions: WorkspaceVersion[];
  onPublishVersion: (version: number) => void;
  readOnly: boolean;
}

type SectionKey = "runtime" | "dependencies" | "secrets" | "published";
type SectionIcon = typeof Cpu;

const PANEL_BORDER = IgGrayPrimitives[500];
const PANEL_TEXT = IgGrayPrimitives["A600"];
const PANEL_MUTED_TEXT = IgGrayPrimitives["A300"];
const PANEL_PLACEHOLDER_TEXT = IgGrayPrimitives["A200"];
const COMPACT_ID_LENGTH = 12;

const formatCompactId = (id: string | null | undefined) =>
  id ? id.slice(-COMPACT_ID_LENGTH) : undefined;

/**
 * Lightweight collapsible section. Deliberately NOT `IgAccordion`: the spec
 * requires "no nested cards", so this renders a flat clickable header + content
 * with a divider between sections rather than a bordered card per section.
 */
const CollapsibleSection = ({
  title,
  icon: Icon,
  open,
  onToggle,
  count,
  children,
}: {
  title: string;
  icon: SectionIcon;
  open: boolean;
  onToggle: () => void;
  count?: number;
  children: ReactNode;
}) => {
  const theme = useTheme();
  return (
    <Stack sx={{ borderBottom: `1px solid ${PANEL_BORDER}` }}>
      <Stack
        direction="row"
        alignItems="center"
        onClick={onToggle}
        sx={{
          cursor: "pointer",
          gap: 1,
          px: "14px",
          py: "10px",
          userSelect: "none",
        }}
      >
        <Icon size={13} color={theme.palette.text.primary} />
        <Stack
          direction="row"
          alignItems="center"
          sx={{ flex: 1, minWidth: 0 }}
        >
          <IgTypography
            sx={{
              color: PANEL_TEXT,
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            {title}
          </IgTypography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1}>
          {count !== undefined ? (
            <Stack
              alignItems="center"
              justifyContent="center"
              sx={{
                bgcolor: theme.igPrimitives.gray[400],
                borderRadius: 1,
                color: PANEL_PLACEHOLDER_TEXT,
                fontFamily: IgFontFamily.code,
                fontSize: 10,
                lineHeight: "14px",
                px: "6px",
                py: "1px",
              }}
            >
              {count}
            </Stack>
          ) : null}
          {open ? (
            <ChevronUp size={12} color={PANEL_PLACEHOLDER_TEXT} />
          ) : (
            <ChevronDown size={12} color={PANEL_PLACEHOLDER_TEXT} />
          )}
        </Stack>
      </Stack>
      {open ? <Stack sx={{ px: "14px", pb: "12px" }}>{children}</Stack> : null}
    </Stack>
  );
};

/** A single key/value metadata row used by the Runtime + Published sections. */
const MetaRow = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <Stack
    direction="row"
    alignItems="center"
    justifyContent="space-between"
    spacing={1}
    sx={{ fontSize: 12, minHeight: 24, py: "3px" }}
  >
    <IgTypography
      sx={{
        color: PANEL_MUTED_TEXT,
        flexShrink: 0,
        fontSize: 12,
        lineHeight: "18px",
      }}
    >
      {label}
    </IgTypography>
    <Stack
      sx={{
        alignItems: "flex-end",
        color: PANEL_TEXT,
        flex: 1,
        minWidth: 0,
        textAlign: "right",
      }}
    >
      {children}
    </Stack>
  </Stack>
);

/**
 * Zone 3 right — the workspace controls panel. Renders four collapsible,
 * compact-density sections (Runtime / Dependencies / Secrets / Published
 * version) with no nested cards. All data + callbacks arrive via
 * {@link ControlsPanelProps}; this component owns only ephemeral UI state
 * (which sections are expanded, the in-progress add-dependency form, and the
 * dependency version currently being inline-edited).
 */
export const ControlsPanel = ({
  config,
  aiChat,
  editorValue,
  dependencies,
  onAddDependency,
  onRemoveDependency,
  onUpdateDependencyVersion,
  availableSecretNames,
  onManageSecrets,
  onApplySource,
  onAiPendingChange,
  activeVersion,
  versions,
  onPublishVersion,
  readOnly,
}: ControlsPanelProps) => {
  const theme = useTheme();
  const [mode, setMode] = useState<"ai" | "settings">(
    aiChat ? "ai" : "settings"
  );

  // Settings-only panels (no authoring assistant) start collapsed to a rail:
  // the sections are reference/advanced material, so the editor gets the width
  // until the user asks for them. Panels with an AI chat start open because the
  // chat is a primary authoring surface, not reference material.
  const [collapsed, setCollapsed] = useState(!aiChat);

  const [openSections, setOpenSections] = useState<Record<SectionKey, boolean>>(
    {
      runtime: true,
      dependencies: true,
      secrets: true,
      published: true,
    }
  );
  const toggleSection = (key: SectionKey) =>
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));

  const dependencyManagement = config.dependencies;
  // A domain that admits no package has no list to show and nothing to add to:
  // the section carries the reason instead. Collapsing the capability into the
  // list once keeps the three places that render it from disagreeing.
  const declarableDependencies =
    dependencyManagement.kind === "enabled" ? dependencies : [];

  // Whether the inline add-dependency form is open.
  const [addingDependency, setAddingDependency] = useState(false);

  // Which dependency (by name) is mid inline-version-edit, plus its draft value.
  const [editingDepName, setEditingDepName] = useImmer<string | null>(null);
  const [editingDepVersion, setEditingDepVersion] = useImmer("");

  // Staging form for a new dependency: validates a non-empty, non-duplicate name
  // before pushing the entry into the shared controls form's `dependencies`.
  const addForm = useAddDependencyForm(
    dependencies.map((dep) => dep.name),
    (dependency) => {
      onAddDependency(dependency);
      setAddingDependency(false);
    }
  );

  const closeAddForm = () => {
    addForm.reset();
    setAddingDependency(false);
  };

  const removeDependency = (name: string) => {
    onRemoveDependency(name);
  };

  const beginEditVersion = (dep: WorkspaceDependency) => {
    if (readOnly) {
      return;
    }
    setEditingDepName(dep.name);
    setEditingDepVersion(dep.version);
  };

  const commitEditVersion = () => {
    if (editingDepName === null) {
      return;
    }
    onUpdateDependencyVersion(
      editingDepName,
      editingDepVersion.trim() || DEFAULT_DEPENDENCY_VERSION
    );
    setEditingDepName(null);
    setEditingDepVersion("");
  };

  // Publish is offered to the most recent non-active published version — the
  // broker can switch its alias to any non-active published version.
  const priorVersions = versions
    .filter((v) => v.id !== activeVersion?.id)
    .sort((a, b) => b.version - a.version);
  const publishTarget = priorVersions[0] ?? null;
  const successIconColor = theme.palette.success.main;

  const panelLabel = mode === "ai" && aiChat ? "AI Chat" : "Settings";

  if (collapsed) {
    return (
      <Stack sx={{ width: RAIL_WIDTH, minWidth: RAIL_WIDTH }}>
        <IgPaper
          sx={{
            p: 0,
            borderRadius: 0,
            height: "100%",
            width: RAIL_WIDTH,
            borderBottom: "0px",
            borderTop: "0px",
            borderLeft: "0px",
          }}
        >
          <Stack
            alignItems="center"
            onClick={() => setCollapsed(false)}
            role="button"
            tabIndex={0}
            aria-label={`Expand ${panelLabel}`}
            sx={{ cursor: "pointer", height: "100%", pt: "8px", gap: "10px" }}
          >
            <ChevronRight size={14} color={PANEL_PLACEHOLDER_TEXT} />
            <IgTypography
              sx={{
                color: PANEL_TEXT,
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: "0.04em",
                userSelect: "none",
                writingMode: "vertical-rl",
              }}
            >
              {panelLabel}
            </IgTypography>
          </Stack>
        </IgPaper>
      </Stack>
    );
  }

  return (
    <Stack
      sx={{
        width: PANEL_WIDTH,
        minWidth: PANEL_WIDTH,
      }}
    >
      <IgPaper
        sx={{
          p: 0,
          borderRadius: 0,
          height: "100%",
          width: PANEL_WIDTH,
          borderBottom: "0px",
          borderTop: "0px",
          borderLeft: "0px",
        }}
      >
        <Stack
          sx={{ height: "100%", overflow: "hidden", position: "relative" }}
        >
          <Tabs
            value={mode}
            onChange={(_, value: "ai" | "settings") => setMode(value)}
            variant="fullWidth"
            sx={{
              borderBottom: `1px solid ${PANEL_BORDER}`,
              minHeight: 36,
              // Leaves room for the absolutely-positioned collapse control.
              pl: "28px",
              "& .MuiTab-root": {
                fontSize: 12,
                fontWeight: 600,
                minHeight: 36,
                py: 0,
                textTransform: "none",
              },
            }}
          >
            {aiChat ? <Tab value="ai" label="AI Chat" /> : null}
            <Tab value="settings" label="Settings" />
          </Tabs>
          <Tooltip title={`Collapse ${panelLabel}`}>
            <IconButton
              aria-label={`Collapse ${panelLabel}`}
              onClick={() => setCollapsed(true)}
              size="small"
              sx={{
                borderBottom: `1px solid ${PANEL_BORDER}`,
                borderRadius: 0,
                position: "absolute",
                left: 0,
                top: 0,
                height: 36,
                width: 28,
              }}
            >
              <ChevronLeft size={14} color={PANEL_PLACEHOLDER_TEXT} />
            </IconButton>
          </Tooltip>

          <Stack sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
            {mode === "ai" && aiChat ? (
              <WorkspaceAiChat
                config={aiChat}
                editorValue={editorValue}
                runtimeContract={config.runtimeContract}
                dependencies={dependencies}
                availableSecretNames={availableSecretNames}
                onApplySource={onApplySource}
                onPendingChange={onAiPendingChange}
              />
            ) : (
              <Stack sx={{ height: "100%", overflow: "auto" }}>
                <CollapsibleSection
                  title="Runtime"
                  icon={Cpu}
                  open={openSections.runtime}
                  onToggle={() => toggleSection("runtime")}
                >
                  <Stack>
                    {config.runtimeFacts.map((fact) => (
                      <MetaRow key={fact.label} label={fact.label}>
                        {fact.mono ? (
                          <IgMonoText
                            value={fact.value}
                            variant="caption"
                            fontWeight={500}
                            sx={{
                              color: PANEL_TEXT,
                              fontSize: 12,
                              lineHeight: "18px",
                            }}
                          />
                        ) : (
                          <IgTypography
                            sx={{
                              color: PANEL_TEXT,
                              fontSize: 12,
                              fontWeight: 500,
                              lineHeight: "18px",
                            }}
                          >
                            {fact.value}
                          </IgTypography>
                        )}
                      </MetaRow>
                    ))}
                  </Stack>
                </CollapsibleSection>

                {/* --- Dependencies ------------------------------------------------- */}
                <CollapsibleSection
                  title="Dependencies"
                  icon={Package}
                  open={openSections.dependencies}
                  onToggle={() => toggleSection("dependencies")}
                  count={declarableDependencies.length}
                >
                  <Stack>
                    {dependencyManagement.kind === "unsupported" ? (
                      // The domain admits no third-party package, so the panel
                      // says why instead of offering a form whose entries the
                      // API refuses.
                      <IgTypography
                        sx={{
                          color: PANEL_PLACEHOLDER_TEXT,
                          fontSize: 12,
                          lineHeight: "18px",
                        }}
                      >
                        {dependencyManagement.reason}
                      </IgTypography>
                    ) : null}

                    {dependencyManagement.kind === "enabled" &&
                    declarableDependencies.length === 0 &&
                    !addingDependency ? (
                      <IgTypography
                        sx={{
                          color: PANEL_PLACEHOLDER_TEXT,
                          fontSize: 12,
                          fontStyle: "italic",
                          lineHeight: "18px",
                        }}
                      >
                        No dependencies
                      </IgTypography>
                    ) : null}

                    {declarableDependencies.map((dep) => (
                      <Stack
                        key={dep.name}
                        direction="row"
                        alignItems="center"
                        spacing={0.625}
                        sx={{
                          fontFamily: IgFontFamily.code,
                          fontSize: 12,
                          py: "4px",
                        }}
                      >
                        <IgMonoText
                          value={dep.name}
                          noWrap
                          variant="caption"
                          sx={{ color: PANEL_TEXT, flex: 1, fontSize: 12 }}
                        />
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={0.5}
                        >
                          {editingDepName === dep.name ? (
                            <IgTextField
                              value={editingDepVersion}
                              autoFocus
                              size="small"
                              onChange={(e) =>
                                setEditingDepVersion(e.target.value)
                              }
                              onBlur={commitEditVersion}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  commitEditVersion();
                                }
                                if (e.key === "Escape") {
                                  setEditingDepName(null);
                                }
                              }}
                              sx={{ width: 64 }}
                            />
                          ) : (
                            <Stack
                              onClick={() => beginEditVersion(dep)}
                              sx={{ cursor: readOnly ? "default" : "pointer" }}
                            >
                              <IgMonoText
                                value={dep.version}
                                noWrap
                                color={PANEL_PLACEHOLDER_TEXT}
                                variant="caption"
                                sx={{
                                  borderBottom: readOnly
                                    ? undefined
                                    : `1px dashed ${theme.igPrimitives.gray[800]}`,
                                  fontSize: 12,
                                  lineHeight: "18px",
                                }}
                              />
                            </Stack>
                          )}
                          <CheckCircle2 size={12} color={successIconColor} />
                          {!readOnly ? (
                            <Stack
                              component="span"
                              onClick={() => removeDependency(dep.name)}
                              aria-label={`Remove ${dep.name}`}
                              sx={{
                                color: theme.igPrimitives.gray["A100"],
                                cursor: "pointer",
                                fontSize: 14,
                                lineHeight: 1,
                              }}
                              title="Remove dependency"
                            >
                              x
                            </Stack>
                          ) : null}
                        </Stack>
                      </Stack>
                    ))}

                    {addingDependency ? (
                      <Stack
                        data-testid="dependency-add-form"
                        spacing={0.75}
                        sx={{
                          bgcolor: theme.igPrimitives.gray[200],
                          border: `1px solid ${theme.igPrimitives.gray[700]}`,
                          borderRadius: 1,
                          mt: "6px",
                          p: "8px 10px",
                        }}
                      >
                        <addForm.Field
                          name="name"
                          validators={{
                            onChange: ({ value }) =>
                              validateNewDependencyName(
                                value,
                                dependencies.map((dep) => dep.name)
                              ),
                          }}
                        >
                          {(field) => (
                            <Stack spacing={0.25}>
                              <IgTextField
                                value={field.state.value}
                                autoFocus
                                size="small"
                                placeholder="package name"
                                error={field.state.meta.errors.length > 0}
                                onChange={(e) =>
                                  field.handleChange(e.target.value)
                                }
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    void addForm.handleSubmit();
                                  }
                                  if (e.key === "Escape") {
                                    closeAddForm();
                                  }
                                }}
                                sx={{
                                  "& .MuiInputBase-input": {
                                    fontFamily: IgFontFamily.code,
                                    fontSize: 12,
                                    py: 0.5,
                                  },
                                }}
                              />
                              {field.state.meta.errors.length > 0 ? (
                                <IgTypography
                                  sx={{
                                    color: theme.palette.error.main,
                                    fontSize: 10,
                                    lineHeight: "14px",
                                  }}
                                >
                                  {String(field.state.meta.errors[0])}
                                </IgTypography>
                              ) : null}
                            </Stack>
                          )}
                        </addForm.Field>
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={0.5}
                        >
                          <addForm.Field name="version">
                            {(field) => (
                              <IgTextField
                                value={field.state.value}
                                size="small"
                                placeholder={DEFAULT_DEPENDENCY_VERSION}
                                onChange={(e) =>
                                  field.handleChange(e.target.value)
                                }
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    void addForm.handleSubmit();
                                  }
                                  if (e.key === "Escape") {
                                    closeAddForm();
                                  }
                                }}
                                sx={{
                                  flex: 1,
                                  "& .MuiInputBase-input": {
                                    fontFamily: IgFontFamily.code,
                                    fontSize: 12,
                                    py: 0.5,
                                  },
                                }}
                              />
                            )}
                          </addForm.Field>
                          <addForm.Subscribe selector={(s) => s.canSubmit}>
                            {(canSubmit) => (
                              <IgButton
                                variant="contained"
                                size="mini"
                                onClick={() => void addForm.handleSubmit()}
                                disabled={!canSubmit}
                              >
                                Add
                              </IgButton>
                            )}
                          </addForm.Subscribe>
                          <IgButton
                            variant="text"
                            size="mini"
                            onClick={closeAddForm}
                          >
                            Cancel
                          </IgButton>
                        </Stack>
                      </Stack>
                    ) : !readOnly && dependencyManagement.kind === "enabled" ? (
                      <IgLink
                        variant="caption"
                        onClick={() => setAddingDependency(true)}
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={0.25}
                          component="span"
                          sx={{ fontSize: 11, lineHeight: "16px", mt: "6px" }}
                        >
                          <Plus size={11} style={{ color: "inherit" }} />
                          <span>Add dependency</span>
                        </Stack>
                      </IgLink>
                    ) : null}
                  </Stack>
                </CollapsibleSection>

                {/* --- Secrets ------------------------------------------------------ */}
                <CollapsibleSection
                  title="Secrets"
                  icon={KeyRound}
                  open={openSections.secrets}
                  onToggle={() => toggleSection("secrets")}
                  count={availableSecretNames.length}
                >
                  <Stack>
                    {availableSecretNames.length === 0 ? (
                      <IgTypography
                        sx={{
                          color: PANEL_PLACEHOLDER_TEXT,
                          fontSize: 12,
                          fontStyle: "italic",
                          lineHeight: "18px",
                        }}
                      >
                        No org secrets
                      </IgTypography>
                    ) : (
                      availableSecretNames.map((name) => (
                        <Stack
                          key={name}
                          direction="row"
                          alignItems="center"
                          spacing={0.75}
                          sx={{ fontSize: 12, py: "4px" }}
                        >
                          <LockKeyhole
                            size={12}
                            color={PANEL_PLACEHOLDER_TEXT}
                          />
                          <IgMonoText
                            value={name}
                            noWrap
                            variant="caption"
                            sx={{ color: PANEL_TEXT, flex: 1, fontSize: 12 }}
                          />
                          <CheckCircle2 size={12} color={successIconColor} />
                        </Stack>
                      ))
                    )}
                    <IgLink variant="caption" onClick={onManageSecrets}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.5}
                        component="span"
                        sx={{ fontSize: 11, lineHeight: "16px", mt: "6px" }}
                      >
                        <ExternalLink size={11} style={{ color: "inherit" }} />
                        <span>Manage org secrets</span>
                      </Stack>
                    </IgLink>
                  </Stack>
                </CollapsibleSection>

                {/* --- Published version -------------------------------------------- */}
                <CollapsibleSection
                  title="Published version"
                  icon={Upload}
                  open={openSections.published}
                  onToggle={() => toggleSection("published")}
                >
                  {activeVersion ? (
                    <Stack>
                      <MetaRow label="Version">
                        <IgMonoText
                          value={`v${activeVersion.version}`}
                          variant="caption"
                          sx={{
                            color: PANEL_TEXT,
                            fontSize: 12,
                            lineHeight: "18px",
                          }}
                        />
                      </MetaRow>
                      <MetaRow label="Published">
                        <IgTypography
                          sx={{
                            color: PANEL_TEXT,
                            fontSize: 12,
                            fontWeight: 500,
                            lineHeight: "18px",
                          }}
                        >
                          {activeVersion.publishedAtTs
                            ? format(
                                new Date(activeVersion.publishedAtTs),
                                "MMM d, yyyy h:mm a"
                              )
                            : "—"}
                        </IgTypography>
                      </MetaRow>
                      <MetaRow label="By">
                        <IgMonoText
                          value={
                            activeVersion.publishedByUser?.name ??
                            formatCompactId(activeVersion.publishedByUserId)
                          }
                          noWrap
                          variant="caption"
                          sx={{
                            color: PANEL_TEXT,
                            fontSize: 12,
                            lineHeight: "18px",
                          }}
                        />
                      </MetaRow>
                      <MetaRow label="Lock">
                        <IgMonoText
                          value={formatCompactId(
                            activeVersion.dependencyLockHash
                          )}
                          noWrap
                          copyable={!!activeVersion.dependencyLockHash}
                          variant="caption"
                          sx={{
                            color: PANEL_TEXT,
                            fontSize: 12,
                            lineHeight: "18px",
                          }}
                        />
                      </MetaRow>
                      {publishTarget && !readOnly ? (
                        <IgButton
                          variant="outlined"
                          size="small"
                          color="error"
                          startIcon={<Upload size={12} />}
                          onClick={() =>
                            onPublishVersion(publishTarget.version)
                          }
                          sx={{ mt: "6px" }}
                        >
                          Publish v{publishTarget.version}
                        </IgButton>
                      ) : null}
                    </Stack>
                  ) : (
                    <IgTypography
                      sx={{
                        color: PANEL_PLACEHOLDER_TEXT,
                        fontSize: 12,
                        fontStyle: "italic",
                        lineHeight: "18px",
                      }}
                    >
                      No published version
                    </IgTypography>
                  )}
                </CollapsibleSection>
              </Stack>
            )}
          </Stack>
        </Stack>
      </IgPaper>
    </Stack>
  );
};
